/**
 * Hierarchical role ranking, in independent per-product ladders.
 *
 *   DEPOT                            125
 *   Depot.Staff.Admin (4)  ┐ full    125.Admin (2)  writes (report builder)
 *   Depot.Admin       (3)  ┘ access  125.User  (1)  reads
 *   Depot.Staff.User  (2)    limited subset
 *   Depot.User        (1)    no access
 *   unknown           (0)    no access (safe default: deny)
 *
 * Ranks are comparable only WITHIN a ladder. A role in one ladder never
 * satisfies a requirement naming a role in another, in either direction: with a
 * single merged map a Depot.Staff.Admin (4) would satisfy a `125.Admin`
 * requirement, and a 125.Admin would satisfy `Depot.Admin`. Two maps make that
 * impossible rather than merely unlikely.
 *
 * NOTE: ranks are matched by EXACT role name — an unrecognized `Depot.Staff.*`
 * role must NOT inherit staff-admin access, so no prefix matching here.
 *
 * The prefix matching in `ladderFor` is a different question from that NOTE: it
 * picks WHICH ladder a name is ranked in, and every rank lookup inside a ladder
 * is still exact-name. `Depot.Staff.Nonsense` resolves to the depot ladder and
 * then ranks 0 within it, which is the deny the NOTE is protecting.
 *
 * This is UX gating only — the API remains the security boundary.
 */
export const ROLE_STAFF_ADMIN = 'Depot.Staff.Admin';
export const ROLE_ADMIN = 'Depot.Admin';
export const ROLE_STAFF_USER = 'Depot.Staff.User';
export const ROLE_USER = 'Depot.User';

export const ROLE_125_ADMIN = '125.Admin';
export const ROLE_125_USER = '125.User';

type Ladder = Record<string, number>;

const DEPOT_RANKS: Ladder = {
	[ROLE_STAFF_ADMIN]: 4,
	[ROLE_ADMIN]: 3,
	[ROLE_STAFF_USER]: 2,
	[ROLE_USER]: 1,
};

const PLATFORM_RANKS: Ladder = {
	[ROLE_125_ADMIN]: 2,
	[ROLE_125_USER]: 1,
};

/** The depot ladder, highest first. The one place its order is written down. */
export const ROLE_ORDER = [ROLE_STAFF_ADMIN, ROLE_ADMIN, ROLE_STAFF_USER, ROLE_USER];

/** Names the ladders so callers can ask which one a requirement belongs to. */
export type LadderName = 'depot' | 'platform';

/**
 * Which ladder a role name belongs to, or null when it belongs to none.
 *
 * Selecting a ladder by prefix, then ranking by exact name within it — see the
 * NOTE above for why those two must not be confused.
 */
export function ladderNameFor(role: string): LadderName | null {
	if (role.startsWith('Depot.')) {
		return 'depot';
	}

	if (role.startsWith('125.')) {
		return 'platform';
	}

	return null;
}

function ladderFor(role: string): Ladder | null {
	const name = ladderNameFor(role);

	if (name === 'depot') {
		return DEPOT_RANKS;
	}

	if (name === 'platform') {
		return PLATFORM_RANKS;
	}

	return null;
}

/** Rank of a role within its own ladder. 0 for a name no ladder recognizes. */
export function roleRank(role: string): number {
	return ladderFor(role)?.[role] ?? 0;
}

/** Highest rank across `roles` counting only those belonging to `ladder`. */
function maxRankIn(roles: string[], ladder: Ladder): number {
	return roles.reduce((max, role) => {
		if (ladderFor(role) !== ladder) {
			return max;
		}

		return Math.max(max, ladder[role] ?? 0);
	}, 0);
}

/**
 * Highest DEPOT rank across `roles` (0 if none recognized). Non-depot roles are
 * ignored, so this cannot be raised by holding a 125 role.
 *
 * Depot-scoped rather than ladder-agnostic because every caller is a depot
 * concern: the preview override, `realRank`, `rank`, `isAdmin` and `isStaff`.
 */
export function maxRank(roles: string[]): number {
	return maxRankIn(roles, DEPOT_RANKS);
}

/**
 * Does any role in `roles` meet `required`, within `required`'s own ladder?
 *
 * The one comparison every gate resolves through. Roles outside `required`'s
 * ladder are ignored, so the caller can hand over every role the user holds and
 * needs no idea which product the requirement belongs to.
 *
 * An unrecognized `required` is refused rather than granted. Without that guard
 * it would rank 0 and `>= 0` would wave everything through — a typo'd
 * `meta.minRole` opening a route to everyone.
 */
export function satisfies(roles: string[], required: string): boolean {
	const requiredRank = roleRank(required);
	const ladder = ladderFor(required);

	if (requiredRank === 0 || !ladder) {
		return false;
	}

	return maxRankIn(roles, ladder) >= requiredRank;
}

/**
 * The roles a UI gate should resolve against: the user's own roles, unless a
 * role preview override is both recognized and strictly lower.
 *
 * Depot-only by design — the override names a depot role, and `maxRank` above
 * reads the depot ladder — so a preview cannot alter what the 125 section shows.
 *
 * The two rejections are the whole safety story, and both matter because the
 * override is persisted to localStorage where anyone can edit it:
 *
 *   - an override that does not reduce rank is ignored, so a hand-edited value
 *     can never grant access the token does not already carry;
 *   - an unrecognized role ranks 0, which would deny the previewer even the
 *     depot dashboard and strand them on Forbidden — a page outside the depot
 *     shell, and so without the control needed to undo the preview.
 *
 * Returns `roles` itself (same reference) whenever the override does not apply.
 */
export function effectiveRoles(roles: string[], override: string | null): string[] {
	// Depot-scoped deliberately. `roleRank` now recognizes 125 roles too, and a
	// bare `roleRank(override)` would score a hand-edited `125.User` override as
	// rank 1 — a valid reduction against any depot rank — and replace the user's
	// depot roles with it, dropping them to depot rank 0 across the whole depot
	// section. A non-depot override ranks 0 here and is ignored, which is the
	// stranding this function's second rejection exists to prevent.
	const overrideRank =
		override && ladderNameFor(override) === 'depot' ? roleRank(override) : 0;

	if (!override || overrideRank === 0 || overrideRank >= maxRank(roles)) {
		return roles;
	}

	return [override];
}
